import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { BreadcrumbService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AuthService } from '@card-software/nexus-utilities';
import { Asset } from '@app/models/asset/asset';
import { AssetSearchCommand } from '@app/models/asset/assetSearchCommand';
import { FinishingRouteProductionCard, Route } from '@app/models/production/finishing-route-production-card';
import { FinishingRoles } from '@app/models/roles/roles-group';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';

@Component({
  selector: 'app-finishing-dashboard-details',
  templateUrl: './finishing-dashboard-details.component.html',
  styleUrl: './finishing-dashboard-details.component.scss',
})
export class FinishingDashboardDetailsComponent implements OnInit {
  currentMachine = signal<Asset | null>(null);
  finishingRouteSelected = signal<FinishingRouteProductionCard | null>(null);
  finishingRouteOptions = signal<FinishingRouteProductionCard[]>([]);
  isLoading = 0;

  get isFinishingAdmin(): boolean {
    return AuthService.hasRole(FinishingRoles.FinishingAdmin);
  }

  // Computed properties for UI state
  canProceedWithAction = computed(() => {
    const validation = this.validateIfCanStartProcess(this.processAvailableOfCurrentStation, this.currentStateOfRoutePC);
    return validation.canProceed;
  });

  buttonLabel = computed(() => {
    const validation = this.validateIfCanStartProcess(this.processAvailableOfCurrentStation, this.currentStateOfRoutePC);
    if (!validation.canProceed) {
      return 'Cannot Proceed';
    }
    return validation.action === 'start' ? `Start Process ${validation.processId}` : `Complete Process ${validation.processId}`;
  });
  private activatedRoute = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);
  private assetProxyService = inject(AssetProxyService);
  private finishingRouteProxyService = inject(FinishingRouteProxyService);
  private router = inject(Router);

  constructor() {
    effect(
      () => {
        if (this.currentMachine()) {
          this.breadcrumbService.set([
            { label: 'Breadcrumb_Finishing' },
            { label: 'Breadcrumb_Stations_Dashboard', route: '/finishing/dashboard' },
            { label: 'Station - ' + this.currentMachine()?.assetId + ':' + this.currentMachine()?.name },
          ]);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadCurrentMachine(this.activatedRoute.snapshot.params['assetId']);
  }

  private loadCurrentMachine(assetId: string) {
    const command: AssetSearchCommand = {
      assetId: assetId,
    };

    this.assetProxyService.filterAssets(command).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const matchAsset = response.data?.find((a) => a.assetId === assetId);

          if (matchAsset) {
            this.currentMachine.set(matchAsset);
          } else {
            GlobalMessageUtil.error('Error', 'Error getting assets');
          }
        }
      },
      error: () => {
        console.log('error');
      },
    });
  }

  searchRouteProductionCard(event: AutoCompleteCompleteEvent) {
    const number = event.query || '';

    if (!number || number.trim() === '') {
      GlobalMessageUtil.error('Error', 'Please enter a valid route of production card');
      return;
    }

    this.isLoading++;
    this.finishingRouteProxyService.getRoutesOfPC(number).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const resp = response.data;
          const matchProcessOfThisStation = this.currentMachine()?.scanningStationDetails?.processIds;
          const match = resp.route.map((r) => r.processId);
          const isMatch = match.some((m) => matchProcessOfThisStation?.includes(m));

          if (isMatch) {
            this.finishingRouteSelected.set(resp);
          } else {
            GlobalMessageUtil.error('Error', 'This station does not match with any process of this route');
          }
        }
        this.isLoading--;
      },
      error: () => {
        console.log('error');
        this.isLoading--;
      },
    });
  }

  startProcess() {
    if (!this.isFinishingAdmin) {
      GlobalMessageUtil.error('Unauthorized', 'You do not have permission to start or complete processes.');
      return;
    }

    const processCurrentStation = this.processAvailableOfCurrentStation;
    const currentStateOfRoutePCSelected = this.currentStateOfRoutePC;
    const validation = this.validateIfCanStartProcess(processCurrentStation, currentStateOfRoutePCSelected);

    console.log('Validation result:', validation);

    // If validation fails, show error and stop
    if (!validation.canProceed) {
      GlobalMessageUtil.error('Cannot Proceed', validation.reason);
      return;
    }

    // Get required data
    const stationId = this.currentMachine()?.assetId;
    const routeNumber = this.finishingRouteSelected()?.number;

    if (!stationId || !routeNumber) {
      GlobalMessageUtil.error('Error', 'Missing station or route information');
      return;
    }

    this.isLoading++;

    // Call API to start/complete the process
    this.finishingRouteProxyService.startActionFinishingRoute(stationId, routeNumber).subscribe({
      next: () => {
        this.isLoading--;
        GlobalMessageUtil.success(
          'Success',
          `Process ${validation.processId} ${validation.action === 'start' ? 'started' : 'completed'} successfully`,
        );

        // Refresh the route data to get updated process states
        this.refreshRouteData(routeNumber);
      },
      error: (error) => {
        this.isLoading--;
        console.error('Error processing action:', error);
      },
    });
  }

  /**
   * Refresh the route data after an action
   */
  private refreshRouteData(routeNumber: string) {
    this.finishingRouteProxyService.getRoutesOfPC(routeNumber).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.finishingRouteSelected.set(response.data);
        }
      },
      error: () => {
        console.error('Failed to refresh route data');
      },
    });
  }

  get processAvailableOfCurrentStation(): string[] {
    return this.currentMachine()?.scanningStationDetails?.processIds?.sort((a, b) => a.localeCompare(b)) || [];
  }

  get currentStateOfRoutePC(): Route | undefined {
    const route = this.finishingRouteSelected()?.route;
    if (!route) return undefined;

    // Find the first process that hasn't been completed yet
    const nextProcess = route.find((r) => r.endTime === null);

    // Validate sequence: ensure all previous processes are completed
    if (nextProcess) {
      const processIndex = route.indexOf(nextProcess);
      const allPreviousCompleted = route.slice(0, processIndex).every((r) => r.endTime !== null);

      if (!allPreviousCompleted) {
        console.warn('Process sequence violation: Previous processes not completed');
        return undefined; // Invalid state
      }
    }

    return nextProcess;
  }

  /**
   * Validates if the current station can start or complete the active process
   * Scenario 1: Check if station can handle the process
   * Scenario 2: Check if process is next in sequence
   */
  validateIfCanStartProcess(
    processStation: string[],
    currentStateOfRoutePC: Route | undefined,
  ): { canProceed: boolean; reason: string; action: 'start' | 'complete' | null; processId?: string } {
    // No active process found
    if (!currentStateOfRoutePC) {
      return {
        canProceed: false,
        reason: 'No active process found in the route or sequence violation detected',
        action: null,
      };
    }

    // Scenario 1: Check if station can handle this process
    const stationCanHandle = processStation.includes(currentStateOfRoutePC.processId);
    if (!stationCanHandle) {
      return {
        canProceed: false,
        reason: `Station cannot handle process ${currentStateOfRoutePC.processId}. This station handles: ${processStation.join(', ')}`,
        action: null,
        processId: currentStateOfRoutePC.processId,
      };
    }

    // Scenario 2: Process sequence is already validated in currentStateOfRoutePC getter
    // Determine action based on process state
    if (currentStateOfRoutePC.startTime && !currentStateOfRoutePC.endTime) {
      // Process already started, should complete it
      return {
        canProceed: true,
        reason: `Process ${currentStateOfRoutePC.processId} is in progress. Ready to complete.`,
        action: 'complete',
        processId: currentStateOfRoutePC.processId,
      };
    } else if (!currentStateOfRoutePC.startTime && !currentStateOfRoutePC.endTime) {
      // Process not started yet, ready to start
      return {
        canProceed: true,
        reason: `Ready to start process ${currentStateOfRoutePC.processId}`,
        action: 'start',
        processId: currentStateOfRoutePC.processId,
      };
    } else {
      // Process already completed (both startTime and endTime exist)
      return {
        canProceed: false,
        reason: `Process ${currentStateOfRoutePC.processId} is already completed`,
        action: null,
        processId: currentStateOfRoutePC.processId,
      };
    }
  }

  cancelOperation() {
    this.router.navigate(['/finishing/dashboard']);
    this.resetValues();
  }

  resetValues() {
    this.finishingRouteSelected.set(null);
    this.currentMachine.set(null);
    this.isLoading = 0;
  }
}
